import { forwardRef, useState, type ImgHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
	fallback?: string | React.ReactNode
	onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void
}

export const Image = forwardRef<HTMLImageElement, ImageProps>(
	({ className, src, fallback, onError, alt, ...props }, ref) => {
		const [hasError, setHasError] = useState(false)
		const [isLoading, setIsLoading] = useState(true)

		const handleError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
			setHasError(true)
			setIsLoading(false)
			onError?.(event)
		}

		const handleLoad = () => {
			setIsLoading(false)
		}

		if (hasError || !src) {
			if (typeof fallback === 'string') {
				return <img ref={ref} src={fallback} alt={alt} className={className} onLoad={handleLoad} {...props} />
			}
			if (fallback) {
				return <div className={cn('flex items-center justify-center', className)}>{fallback}</div>
			}
			// Default fallback
			return <DefaultFallback className={className} />
		}

		return (
			<img
				ref={ref}
				src={src}
				alt={alt}
				className={cn(className, isLoading && 'opacity-50')}
				onError={handleError}
				onLoad={handleLoad}
				{...props}
			/>
		)
	}
)

function DefaultFallback({ className }: { className?: string }) {
	return (
		<div className={cn('flex items-center justify-center bg-muted text-muted-foreground', className)}>
			<svg
				className='h-8 w-8'
				fill='none'
				stroke='currentColor'
				viewBox='0 0 24 24'
				xmlns='http://www.w3.org/2000/svg'
			>
				<path
					strokeLinecap='round'
					strokeLinejoin='round'
					strokeWidth={2}
					d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
				/>
			</svg>
		</div>
	)
}
